import React, { useState, useEffect } from "react"
import api from "../services/api"
import { Helmet } from "react-helmet"
import { Eye, EyeOff } from "lucide-react"
import Swal from "sweetalert2"
import { useLocation, useNavigate } from "react-router-dom"
import { auth } from "../auth/auth"

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({ email: "", password: "" })

  const validate = () => {
    const newErrors = {}
    if (!formData.email.trim()) newErrors.email = "Email is required"
    if (!formData.password.trim()) newErrors.password = "Password is required"
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    setErrors((prev) => {
      if (!prev[name]) return prev
      const { [name]: _, ...rest } = prev
      return rest
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    const token = window.turnstile?.getResponse()

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Verification required",
        text: "Please verify you are human first.",
        confirmButtonColor: "#f97316",
      })
      return
    }

    setLoading(true)

    Swal.fire({
      title: "Signing in...",
      text: "Please wait",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    })

    try {
      const res = await api.post(`/api/auth/login`, {
        email: formData.email,
        password: formData.password,
        turnstileToken: token,
      })

      const authToken = res.data?.token

      if (!authToken) throw new Error("No token returned by server.")

      const expiresAt = Date.now() + 15 * 60 * 1000
      auth.setToken(authToken, expiresAt)

      // ✅ IMPORTANT: fetch the full user profile (includes avatarUrl)

      const meRes = await api.get(`/api/auth/me`)

      // If your backend returns { user: {...} } then use meRes.data.user
      const fullUser = meRes.data?.user ?? meRes.data

      auth.setUser(fullUser)

      Swal.close()
      Swal.fire({
        icon: "success",
        title: "Welcome!",
        text: "Login successful.",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })

      const redirectTo = location.state?.from?.pathname || "/units"
      navigate(redirectTo, { replace: true })
    } catch (err) {
      Swal.close()

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Invalid email or password."

      // cleanup
      auth.logout()

      Swal.fire({
        icon: "error",
        title: "Login failed",
        text: msg,
        confirmButtonColor: "#f97316",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (Object.keys(errors).length === 0) return
    const timer = setTimeout(() => setErrors({}), 5000)
    return () => clearTimeout(timer)
  }, [errors])

  useEffect(() => {
    if (!window.turnstile) return

    const widgetId = window.turnstile.render("#turnstile-container", {
      sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
      theme: "dark",
      size: "flexible",
    })

    return () => {
      if (widgetId) {
        window.turnstile.remove(widgetId)
      }
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>Login - Stay Suite</title>
      </Helmet>

      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center p-6 overflow-y-hidden"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1920&q=80')",
        }}
      >
        <div className="fixed inset-0 bg-black/50" />

        <div className="relative w-full max-w-md min-w-[320px] rounded-xl border border-white/20 bg-white/90 backdrop-blur shadow-2xl p-6 animate-fadeIn -mt-30">
          <div className="text-center mb-2">
            <img
              className="w-full h-16 md:h-18 object-contain mb-5"
              src="/images/icons/STAY-SUITE_LOGO_DARK-V1.webp"
              alt="Logo"
            />
            <p className="text-sm text-gray-600 mt-1">
              Admin Portal • Secure Login
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
            autoComplete="off"
          >
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="mail@protonmail.com"
                className={`w-full rounded-lg border px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                transition ${errors.email ? "border-red-500" : "border-gray-300"}`}
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••••••"
                  className={`w-full rounded-lg border px-3 py-2 pr-10 text-sm
                  focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                  transition ${errors.password ? "border-red-500" : "border-gray-300"}`}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-900"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password}</p>
              )}
            </div>
            <div className="w-full flex justify-center overflow-hidden">
              <div id="turnstile-container" className="w-full max-w-[320px]" />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full px-4 py-2 rounded-lg text-sm font-semibold
              bg-orange-600 text-white hover:bg-orange-700
              focus:ring-2 focus:ring-orange-500 focus:ring-offset-1
              transition ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <div className="text-center mt-2">
              <a
                href="/home"
                className="text-sm text-orange-600 hover:underline"
              >
                Go to home
              </a>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
